import { useState, useCallback, useMemo } from 'react';
import _ from 'lodash';

type SortDirection = 'asc' | 'desc';

type Sorter =
  | string
  | ((record: Record<string, any>, direction: SortDirection) => any);

export interface SortConfig {
  key: string;
  sorter: Sorter;
  direction: SortDirection;
}

const getSortValue = (
  row: Record<string, any>,
  sorter: Sorter,
  direction: SortDirection,
) => {
  let value;
  if (typeof sorter === 'string') {
    value = _.get(row, sorter);
  } else {
    value = sorter(row, direction);
  }
  return value;
};

function useSort({ data }: { data?: Record<string, any>[] }) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const handleSortColumn = useCallback((key, sorter) => {
    setSortConfig(prev => {
      if (prev === null || prev.key !== key) {
        return {
          key,
          sorter,
          direction: 'asc',
        };
      }

      if (prev.direction === 'asc') {
        return {
          ...prev,
          direction: 'desc',
        };
      }

      if (prev.direction === 'desc') {
        return null;
      }
      return null;
    });
  }, []);

  const sortedData = useMemo(() => {
    if (!sortConfig) return data || [];
    return _.orderBy(
      data,
      [
        // always sort null and undefined to last
        row => {
          const value = getSortValue(
            row,
            sortConfig.sorter,
            sortConfig.direction,
          );
          return value === null || value === undefined ? Infinity : 0;
        },
        row => getSortValue(row, sortConfig.sorter, sortConfig.direction),
      ],
      ['asc', sortConfig.direction],
    );
  }, [data, sortConfig]);

  return {
    sortConfig,
    sortedData,
    handleSortColumn,
  };
}

export default useSort;
